import { Component, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, Subject, Subscription, lastValueFrom, takeUntil } from 'rxjs';
import Papa from 'papaparse';
import { BackendApiService } from 'src/app/core/services/backend-api';
import {
  PurchaseExecutiveListResponse,
  PurchaseExecutiveUser,
  RFQReportRequest,
  RfqSummaryReportRow,
  RFQDetailsFilter
} from 'src/app/shared/models/database-models/PurchaseExecutivesReport.model';
import moment, { Moment } from 'moment';
import { AlertType } from 'src/app/shared/components/alert/alert.component';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import {
  DropDownOptions,
  StoreDropdowns,
  UserInformation,
} from 'src/app/shared/models/app-models';
import { Store } from '@ngrx/store';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { MY_DATE_FORMAT } from 'src/app/data/constants/Data';
import { isMPDocumentNumberValid, isRFQSummaryDocumentNumberValid } from '../shared/utils/common-validation';
import { pageName } from 'src/app/shared/services/page.service';
import RFQSummaryStatus from 'src/app/data/constants/RFQSummaryStatus.json';
import { PurchaseExecutives } from 'src/app/shared/models/database-models/PurchaseExecutives.model';
import { LoadingState } from 'src/app/core/store/loading.reducer';
import { decrementLoading, incrementLoading } from 'src/app/core/store/loading.action';
import { DatePipe } from '@angular/common';

export const RFQ_HEADER_KEYS = [
  'RFQSummaryNumber',
  'DocumentStatus',
  'RequestingOfficerName',
  'PurchaseExecutiveName',
  'CreatedOnDate',
  'SubmittedDate',
  'MPDocumentNumber',
  'RFQNumber',
  'VendorName',
  'QuotationAttachment',
  'ReasonForNoQuotationName',
  'ItemNumber',
  'MaterialGroup',
  'MaterialSubGroup',
  'ItemDescription',
  'BrandOEMName',
  'PartModelNumber',
  'UnitOfMeasurement',
  'PlannedDeliveryDate',
  'Criticality',
  'Quantity',
  'POQuantity',
  'BalanceQuantity',
  'PONumber',
  'AwardedVendorName',
  'POStatus'
] as const;

export type RFQReportHeaderKey = (typeof RFQ_HEADER_KEYS)[number];

export const RFQ_HEADER_MAPPING: { [key in RFQReportHeaderKey]: string } = {
  RFQSummaryNumber: 'RFQ Summary No.',
  DocumentStatus: 'RS Doc Status',
  RequestingOfficerName: 'RO Name',
  PurchaseExecutiveName: 'Assigned PE',
  CreatedOnDate: 'Created Date',
  SubmittedDate: 'Submitted Date',
  MPDocumentNumber: 'MP Doc no.',
  RFQNumber: 'RFQ Doc no.',
  VendorName: 'Name of vendors invited',
  QuotationAttachment: 'Vendor responded?',
  ReasonForNoQuotationName: 'Reason for no bid',
  ItemNumber: 'RS Item No.',
  MaterialGroup: 'Material Group',
  MaterialSubGroup: 'Material Sub Group',
  ItemDescription: 'Item Description',
  BrandOEMName: 'Brand Name/OEM',
  PartModelNumber: 'Part No./Model No.',
  UnitOfMeasurement: 'UoM',
  PlannedDeliveryDate: 'Planned Delivery Date',
  Criticality: 'Criticality Code',
  Quantity: 'RFQ Qty',
  POQuantity: 'PO Qty',
  BalanceQuantity: 'Bal Qty',
  PONumber: 'PO No.',
  AwardedVendorName: 'Awarded Vendor',
  POStatus: 'PO Status'
};

interface FiltersForm {
  csvHeaderPreset: string;
  csvHeaderKeys: boolean[];
  MPDocumentNumber: string;
  purchaseExecutives: string[];
  requestingOfficer: string[];
  startDate: Moment | null;
  endDate: Moment | null;
  RFQSummaryNumber: string[];
  RFQStatus: string[];
}

enum ValidationError {
  INVALID_MP_DOCUMENT_NUMBER = 'INVALID_MP_DOCUMENT_NUMBER',
  CUSTOM_MISSING_START_END_DATE = 'CUSTOM_MISSING_START_END_DATE',
  MINIMUM_ONE_CSV_HEADER = 'MINIMUM_ONE_CSV_HEADER',
  MAXIMUM_6_MONTHS = 'MAXIMUM_6_MONTHS',
  INVALID_RFQ_Summary_DOCUMENT_NUMBER = 'INVALID_RFQ_Summary_DOCUMENT_NUMBER',
}

@Component({
  selector: 'app-rfq-report-page',
  templateUrl: './rfq-report-page.component.html',
  styleUrls: ['./rfq-report-page.component.scss'],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMAT },
  ],
})
export class RfqReportPageComponent implements OnInit, OnDestroy {
  destroy$: Subject<boolean> = new Subject<boolean>();
  activePage: string = pageName.RFQ_Summary_Report;
  userDropdown: DropDownOptions[] = [];
  peDropdown: DropDownOptions[] = [];
  rfqStatusDropdown: DropDownOptions[] = [];
  private fetchedPurchaseExecutivesList = new BehaviorSubject<
    PurchaseExecutiveUser[] | null
  >(null);
  public purchaseExecutivesList$ =
    this.fetchedPurchaseExecutivesList.asObservable();
  private loadingSubscription: Subscription = new Subscription();
  isLoading = false;
  isDownloading = false;
  downloadErrorMessage: string = '';
  private uniqueFiltersStorageKey: string = 'RFQReportsPage-filters';
  private csvFileNamePrefix: string = 'RFQSummary-';
  filtersForm = new FormGroup({
    csvHeaderKeys: new FormArray<FormControl<boolean>>([]),
    MPDocumentNumber: new FormControl<string>('', { nonNullable: true }),
    purchaseExecutives: new FormControl<string[]>([], { nonNullable: true }),
    requestingOfficer: new FormControl<string[]>([], { nonNullable: true }),
    startDate: new FormControl<Moment | null>(moment().subtract(183, 'days')),
    endDate: new FormControl<Moment | null>(moment()),
    RFQSummaryNumber: new FormControl<string[]>([], { nonNullable: true }),
    RFQStatus: new FormControl<string[]>([], { nonNullable: true }),
  });
  isEveryPurchaseExecutiveChecked: boolean = false;
  isEveryRequestingOfficerChecked: boolean = false;
  isEveryRFQStatusChecked: boolean = false;
  isEveryRFQSummaryNumChecked: boolean = false;
  defaultPurchaseExecutivesDropdownPlaceholder = 'Filter by PE name';
  defaultRequestingOfficerPlaceholder = 'Filter by RO name';
  defaultRFQStatusPlaceholder = 'Filter by RFQ Summary Status';
  defaultRFQSummaryNumberPlaceholder = 'Filter by RFQ Summary Number';
  selectedPurchaseExecutivesLabel: string =
    this.defaultPurchaseExecutivesDropdownPlaceholder;
  selectedRequestingOfficerLabel: string =
    this.defaultRequestingOfficerPlaceholder;
  selectedRFQStatusLabel: string =
    this.defaultRFQStatusPlaceholder;
  selectedRFQSummaryNumberLabel: string = this.defaultRFQSummaryNumberPlaceholder;
  isUserPE: boolean = false;
  loggedInUser!: UserInformation;
  RFQSummaryNumItem: DropDownOptions[] = [];

  constructor(
    private BackendApi: BackendApiService,
    private datePipe: DatePipe,
    private store: Store<{
      dp: StoreDropdowns,
      user: UserInformation,
      loading: LoadingState
    }>
  ) {
    this.loadingSubscription = this.store.select('loading').subscribe((loadingState) => {
      this.isLoading = loadingState.loadingStates['rfqSummaryReport'] > 0;
    });
    for (const key of RFQ_HEADER_KEYS) {
      (this.filtersForm.get('csvHeaderKeys') as FormArray).push(
        new FormControl(true)
      );
    }
    this.restoreStoredFilters();
    this.store.dispatch(incrementLoading({ context: 'rfqSummaryReport' }));
    this.store.select('user').pipe(takeUntil(this.destroy$)).subscribe((user: UserInformation) => {
      this.loggedInUser = user;
      if (this.loggedInUser.userName) {
        this.store.dispatch(decrementLoading({ context: 'rfqSummaryReport' }));
        this.loadGetPurchaseExecutivesList()
      }
    });
    this.purchaseExecutivesList$
      .pipe(takeUntil(this.destroy$))
      .subscribe((peList) => {
        if (peList) {
          this.peDropdown = peList.map((pe) => ({
            id: pe.Username?.toLowerCase(),
            value: pe.DisplayName,
            groupBy: 'Select All',
          }));
          this.filtersForm.get('purchaseExecutives')!.setValue(this.peDropdown.map(item => item.id));
        }
        this.removeUnavailableSelectedUsers();
        this.everyPurchaseExecutiveIsChecked();
        this.updateSelectedPurchaseExecutivesLabel();
      });
    this.getRFQStatus();
  }

  async ngOnInit(): Promise<void> {
    this.filtersForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((values) => {
        this.storeFilters();

        this.everyPurchaseExecutiveIsChecked();
        this.everyRequestingOfficerIsChecked();
        this.everyRFQStatusChecked();
        this.updateSelectedPurchaseExecutivesLabel();
        this.updateSelectedRequestingOfficerLabel();
        this.updateSelectedRFQStatusLabel();
        if (this.RFQSummaryNumItem.length > 0) {
          this.everyRFQSummaryNumberChecked();
          this.updateSelectedRFQSummaryNumberLabel();
        }
      });
    this.csvHeaderKeys.valueChanges.subscribe(() => {
    });

    await this.fetchPurchaseExecutivesList();
  }

  async loadGetPurchaseExecutivesList() {
    this.store.dispatch(incrementLoading({ context: 'rfqSummaryReport' }));
    let data: PurchaseExecutives[];
    data = await lastValueFrom(this.BackendApi.getPurchaseExecutivesList(this.loggedInUser.userName))
    this.isUserPE = data.length >= 1 ? true : false;
    this.store
      .select('dp')
      .pipe(takeUntil(this.destroy$))
      .subscribe((dp: StoreDropdowns) => {
        if (dp.UserList.length) {
          this.store.dispatch(decrementLoading({ context: 'rfqSummaryReport' }));
          this.userDropdown = dp.UserList.map((user) => ({
            ...user,
            id: user.id?.toLowerCase(),
            value: user.value,
            groupBy: 'Select All',
          }));
          setTimeout(() => {
            if (this.isUserPE) {
              this.filtersForm.get('requestingOfficer')!.setValue(this.userDropdown.map(item => item.id));
            } else {
              const requestingOfficerValue = this.userDropdown.find(item => item.value === this.loggedInUser.fullName);
              if (requestingOfficerValue) {
                this.filtersForm.get('requestingOfficer')!.setValue([requestingOfficerValue.id]);
                this.filtersForm.get('requestingOfficer')!.disable();
              }
            }
            this.updateSelectedRequestingOfficerLabel();
          }, 1000);
        }
        this.removeUnavailableSelectedUsers();
        this.everyRequestingOfficerIsChecked();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
    this.loadingSubscription.unsubscribe();
  }

  get csvHeaderKeys(): FormArray {
    return this.filtersForm.get('csvHeaderKeys') as FormArray;
  }

  async getRFQStatus() {
    this.rfqStatusDropdown = RFQSummaryStatus.map((status) => ({
      ...status,
      id: status.id,
      value: status.value,
      groupBy: 'Select All',
    }));
    this.filtersForm.get('RFQStatus')!.setValue(this.rfqStatusDropdown.map(item => item.id));
    this.updateSelectedRFQStatusLabel();
  }

  removeUnavailableSelectedUsers() {
    const selectedPEs = this.filtersForm.get('purchaseExecutives')?.value;
    const selectedRO = this.filtersForm.get('requestingOfficer')?.value;

    if (
      Array.isArray(selectedPEs) &&
      Array.isArray(this.peDropdown) &&
      this.peDropdown.length > 0
    ) {
      const uniquePEs = new Set(this.peDropdown.map((pe) => pe.id));
      const availableSelectedPEs = selectedPEs.filter((pe) =>
        uniquePEs.has(pe)
      );

      if (selectedPEs.length !== availableSelectedPEs.length) {
        this.filtersForm
          .get('purchaseExecutives')
          ?.setValue(availableSelectedPEs);
      }
    }

    if (
      Array.isArray(selectedRO) &&
      Array.isArray(this.userDropdown) &&
      this.userDropdown.length > 0
    ) {
      const uniqueROs = new Set(this.userDropdown.map((user) => user.value));
      const availableSelectedRO = selectedRO.filter((ro) =>
        uniqueROs.has(ro)
      );
      if (selectedRO.length !== availableSelectedRO.length) {
        this.filtersForm
          .get('requestingOfficer')
          ?.setValue(availableSelectedRO);
      }
    }
  }

  async breadcrumbsButtonActionHandler($event: any) {
    switch ($event.actionType as string) {
      case 'rfq-download-report':
        this.downloadRFQReport();
        break;
      case 'rfq-reset-filter':
        this.resetRFQFilter();
        break;
      default:
        // do nothing
        break;
    }
  }

  async fetchPurchaseExecutivesList() {
    this.store.dispatch(incrementLoading({ context: 'rfqSummaryReport' }));
    try {
      const response: PurchaseExecutiveListResponse = await lastValueFrom(
        this.BackendApi.getPurchaseExecutives()
      );

      if (!response || !Array.isArray(response)) {
        throw new Error('Empty response or not array of PEs');
      }

      this.fetchedPurchaseExecutivesList.next(response);
    } catch (err: any) {
      console.log(
        'ReportsPage fetchPurchaseExecutivesList failed due to error',
        err
      );
    } finally {
      this.store.dispatch(decrementLoading({ context: 'rfqSummaryReport' }));
    }
  }

  restoreStoredFilters() {
    // Restore any stored filters selected previously
    const storedFilters = localStorage.getItem(this.uniqueFiltersStorageKey);

    if (!storedFilters) {
      return;
    }

    const storedFiltersObj = JSON.parse(storedFilters);

    for (const key of Object.keys(storedFiltersObj)) {
      const value = storedFiltersObj[key];
      if (!value || !(key in this.filtersForm.value)) {
        // Only overwrite default filters if there is stored value
        delete storedFiltersObj[key];
      } else if (key === 'csvHeaderKeys') {
        // If there are changes to this functionality in the future and header keys change, remove obsolete stored header keys
        if (value.length !== this.csvHeaderKeys.value.length) {
          delete storedFiltersObj[key];
        }
      } else if (key === 'startDate' || key === 'endDate') {
        storedFiltersObj[key] = moment(value);
      }
    }

    let initialFilters: FiltersForm = {
      ...this.filtersForm.value,
      ...storedFiltersObj,
    };

    this.filtersForm.setValue(initialFilters);
  }

  updateSelectedPurchaseExecutivesLabel() {
    const selectedPEUsernames =
      this.filtersForm.get('purchaseExecutives')?.value;
    if (
      !Array.isArray(this.peDropdown) ||
      this.peDropdown.length === 0 ||
      !selectedPEUsernames ||
      selectedPEUsernames.length === 0
    ) {
      this.selectedPurchaseExecutivesLabel =
        this.defaultPurchaseExecutivesDropdownPlaceholder;
      return;
    }
    if (this.isEveryPurchaseExecutiveChecked) {
      this.selectedPurchaseExecutivesLabel = 'All';
      return;
    }

    let label = this.peDropdown
      .filter((pe) => selectedPEUsernames.includes(pe.id))
      .map((pe) => pe.value)
      .join(', ');
    if (!label) {
      label = this.defaultPurchaseExecutivesDropdownPlaceholder;
    }
    this.selectedPurchaseExecutivesLabel = label;
  }

  updateSelectedRFQStatusLabel() {
    const selectedRFQStatus =
      this.filtersForm.get('RFQStatus')?.value;
    if (
      !Array.isArray(this.rfqStatusDropdown) ||
      this.rfqStatusDropdown.length === 0 ||
      !selectedRFQStatus ||
      selectedRFQStatus.length === 0
    ) {
      this.selectedRFQStatusLabel =
        this.defaultRFQStatusPlaceholder;
      return;
    }
    if (this.isEveryRFQStatusChecked) {
      this.selectedRFQStatusLabel = 'All';
      return;
    }

    let label = this.rfqStatusDropdown
      .filter((status) => selectedRFQStatus.includes(status.id))
      .map((status) => status.value)
      .join(', ');
    if (!label) {
      label = this.defaultRFQStatusPlaceholder;
    }
    this.selectedRFQStatusLabel = label;
  }

  updateSelectedRFQSummaryNumberLabel() {
    const selectedRFQSummary =
      this.filtersForm.get('RFQSummaryNumber')?.value;
    if (
      !Array.isArray(this.RFQSummaryNumItem) ||
      this.RFQSummaryNumItem.length === 0 ||
      !selectedRFQSummary ||
      selectedRFQSummary.length === 0
    ) {
      this.selectedRFQSummaryNumberLabel =
        this.defaultRFQSummaryNumberPlaceholder;
      return;
    }
    if (this.isEveryRFQSummaryNumChecked) {
      this.selectedRFQSummaryNumberLabel = 'All';
      return;
    }

    let label = this.RFQSummaryNumItem
      .filter((status) => selectedRFQSummary.includes(status.id))
      .map((status) => status.value)
      .join(', ');
    if (!label) {
      label = this.defaultRFQSummaryNumberPlaceholder;
    }
    this.selectedRFQSummaryNumberLabel = label;
  }

  updateSelectedRequestingOfficerLabel() {
    const selectedROUsernames =
      this.filtersForm.get('requestingOfficer')?.value;
    if (
      !Array.isArray(this.userDropdown) ||
      this.userDropdown.length === 0 ||
      !selectedROUsernames ||
      selectedROUsernames.length === 0
    ) {
      this.selectedRequestingOfficerLabel =
        this.defaultRequestingOfficerPlaceholder;
      return;
    }
    if (this.isEveryRequestingOfficerChecked) {
      this.selectedRequestingOfficerLabel = 'All';
      return;
    }

    let label = this.userDropdown
      .filter((ro) => selectedROUsernames.includes(ro.id))
      .map((ro) => ro.value)
      .join(', ');
    if (!label) {
      label = this.defaultRequestingOfficerPlaceholder;
    }

    this.selectedRequestingOfficerLabel = label;
  }

  storeFilters() {
    const storedFilters =
      localStorage.getItem(this.uniqueFiltersStorageKey) ?? '{}';
    const storedFiltersObj: FiltersForm = JSON.parse(storedFilters);

    const newFiltersObj = { ...storedFiltersObj, ...this.filtersForm.value };
    const newFiltersString = JSON.stringify(newFiltersObj);
    localStorage.setItem(this.uniqueFiltersStorageKey, newFiltersString);
  }

  resetRFQFilter() {
    this.filtersForm.reset({
      MPDocumentNumber: '',
      purchaseExecutives: this.peDropdown?.map(item => item.id),
      requestingOfficer: this.userDropdown?.map(item => item.id),
      startDate: moment().subtract(183, 'days'),
      endDate: moment(),
      RFQSummaryNumber: [],
      RFQStatus: this.rfqStatusDropdown?.map(item => item.id),
    });
    this.csvHeaderKeys.controls.forEach((control) => {
      control.setValue(true);
    });
  }


  validateFilters(filters: Partial<FiltersForm>) {
    Object.keys(this.filtersForm.controls).forEach((key) => {
      const control = this.filtersForm.get(key);
      control?.setErrors(null);
    });

    const {
      MPDocumentNumber,
      startDate,
      endDate,
      csvHeaderKeys
    } = filters;
    const diffInMonths = endDate ? endDate.diff(startDate, 'months', true) : 0;

    if (MPDocumentNumber && !isMPDocumentNumberValid(MPDocumentNumber)) {
      this.filtersForm
        .get('MPDocumentNumber')
        ?.setErrors({ [ValidationError.INVALID_MP_DOCUMENT_NUMBER]: true });
    }

    if (!startDate || !endDate) {
      this.filtersForm
        .get('startDate')
        ?.setErrors({ [ValidationError.CUSTOM_MISSING_START_END_DATE]: true });
    }

    else if (
      endDate &&
      startDate &&
      Math.round(diffInMonths) > 6
    ) {
      this.filtersForm
        .get('startDate')
        ?.setErrors({ [ValidationError.MAXIMUM_6_MONTHS]: true });
    }

    if (
      csvHeaderKeys?.every((key) => !key)
    ) {
      this.filtersForm
        .get('csvHeaderKeys')
        ?.setErrors({ [ValidationError.MINIMUM_ONE_CSV_HEADER]: true });
    }

    return { isSuccess: !this.filtersForm.invalid };
  }

  async downloadRFQReport() {
    this.downloadErrorMessage = '';

    const formValue = this.filtersForm.value;
    const { isSuccess } = this.validateFilters(formValue);

    if (!isSuccess) {
      return;
    }

    const todayDate = moment().toDate();
    const lastMonthDate = moment()
      .subtract('30', 'days')
      .startOf('day')
      .toDate();

    // Format request body
    let reqbody: RFQReportRequest['filters'] = {
      MPDocumentNumber: formValue.MPDocumentNumber
        ? formValue.MPDocumentNumber
        : null,
      RFQSummaryNumber: formValue.RFQSummaryNumber && formValue.RFQSummaryNumber.length > 0
        ? formValue.RFQSummaryNumber
        : null,
      purchaseExecutives:
        formValue.purchaseExecutives && formValue.purchaseExecutives.length > 0
          ? formValue.purchaseExecutives
          : null,
      requestingOfficer: formValue.requestingOfficer && formValue.requestingOfficer.length > 0
        ? formValue.requestingOfficer
        : null,
      startDate:
        formValue.startDate?.toDate() ?? lastMonthDate,
      endDate:
        formValue.endDate?.toDate() ?? todayDate,
      RFQStatus: formValue.RFQStatus ? formValue.RFQStatus : null
    };

    // this.store.dispatch(incrementLoading({ context: 'rfqSummaryReport' }));
    this.isDownloading = true;

    try {
      let reportData: RfqSummaryReportRow[] = await lastValueFrom(
        this.BackendApi.getRFQSummaryReport(reqbody).pipe(
          takeUntil(this.destroy$)
        )
      );

      if (Array.isArray(reportData) && reportData.length > 0) {
        this.formatAndSaveReport(reportData);
      } else {
        this.downloadErrorMessage =
          `<b>There is no record in the report. Please ensure that you have:</b><ul><li>Selected an option for every Filter and Report Columns field</li>
        <li>Selected a date range within 6 months</li><li>Selected a RFQ Summary no. within the selected date range</li></ul>`;
      }
    } catch (err) {
      console.log(
        'Error encountered while downloading purchase executives report',
        err
      );
      this.downloadErrorMessage =
        'Something went wrong during the report download, please try again later.';
    } finally {
      // this.store.dispatch(decrementLoading({ context: 'rfqSummaryReport' }));
      this.isDownloading = false;
    }
  }

  private everyPurchaseExecutiveIsChecked() {
    if (this.peDropdown.length === 0) {
      this.isEveryPurchaseExecutiveChecked = false;
      return;
    }
    this.isEveryPurchaseExecutiveChecked = this.peDropdown.every((option) =>
      this.filtersForm.get('purchaseExecutives')!.value.includes(option.id)
    );
  }

  private everyRFQStatusChecked() {
    if (this.rfqStatusDropdown.length === 0) {
      this.isEveryRFQStatusChecked = false;
      return;
    }
    this.isEveryRFQStatusChecked = this.rfqStatusDropdown.every((option) =>
      this.filtersForm.get('RFQStatus')!.value.includes(option.id)
    );
  }

  private everyRFQSummaryNumberChecked() {
    if (this.RFQSummaryNumItem.length === 0) {
      this.isEveryRFQSummaryNumChecked = false;
      return;
    }
    this.isEveryRFQSummaryNumChecked = this.RFQSummaryNumItem.every((option) =>
      this.filtersForm.get('RFQSummaryNumber')!.value.includes(option.id)
    );
  }

  private everyRequestingOfficerIsChecked() {
    if (this.userDropdown.length === 0) {
      this.isEveryRequestingOfficerChecked = false;
      return;
    }
    this.isEveryRequestingOfficerChecked = this.userDropdown.every((option) =>
      this.filtersForm.get('requestingOfficer')!.value.includes(option.id)
    );
  }

  isSelectAllColumnSelected(): boolean {
    return this.csvHeaderKeys.controls.every((control) => control.value);
  }

  isPartialSelection(): boolean {
    const selectedCount = this.csvHeaderKeys.controls.filter((control) => control.value).length;
    return selectedCount > 0 && selectedCount < this.csvHeaderKeys.length;
  }

  toggleAllDisplayColumn(event: any): void {
    const checked = event.checked;
    this.csvHeaderKeys.controls.forEach((control) => control.setValue(checked));
  }


  async onRFQSummaryNumBlur(event: any) {
    let rfqSummaryNum = event.target.value;
    this.filtersForm.get('RFQSummaryNumber')!.setValue([rfqSummaryNum]);
    if (rfqSummaryNum && isRFQSummaryDocumentNumberValid(rfqSummaryNum)) {
      const payload = {
        "MPDocumentNumber": "",
        "RFQSummaryNumber": rfqSummaryNum
      };
      try {
        this.store.dispatch(incrementLoading({ context: 'rfqSummaryReport' }));
        const data: RFQDetailsFilter[] = await lastValueFrom(this.BackendApi.getRFQSummaryListFilter(payload));
        if (data && data.length > 0) {
          this.filtersForm.get('MPDocumentNumber')!.setValue(data[0].MPDocumentNumber);
        }
      }
      catch (err) {
        console.log(err);
      }
      finally {
        this.store.dispatch(decrementLoading({ context: 'rfqSummaryReport' }));
      }
    }

  }

  async onMPDocNumBlur(event: any) {
    let MPDocNum = event.target.value;
    if (MPDocNum && isMPDocumentNumberValid(MPDocNum)) {
      const payload = {
        "MPDocumentNumber": MPDocNum,
        "RFQSummaryNumber": ""
      };
      try {
        this.store.dispatch(incrementLoading({ context: 'rfqSummaryReport' }));
        const data: RFQDetailsFilter[] = await lastValueFrom(this.BackendApi.getRFQSummaryListFilter(payload));
        this.getRFQSummaryNumDropdown(data);
      }
      catch (err) {
        console.log(err);
      }
      finally {
        this.store.dispatch(decrementLoading({ context: 'rfqSummaryReport' }));
      }
    }
  }

  getRFQSummaryNumDropdown(data: RFQDetailsFilter[]) {
    if (this.isUserPE) {
      this.RFQSummaryNumItem = data.map((item) => ({
        id: item.RFQSummaryNumber,
        value: item.RFQSummaryNumber,
        groupBy: 'Select All'
      }));
    } else {
      this.RFQSummaryNumItem = data
        .filter(item => item.RequestingOfficer === this.loggedInUser.userName)
        .map(item => ({
          id: item.RFQSummaryNumber,
          value: item.RFQSummaryNumber,
          groupBy: 'Select All'
        }));
    }
    if (this.RFQSummaryNumItem?.length && this.RFQSummaryNumItem.length === 1) {
      const rfqSummaryNum = this.RFQSummaryNumItem[0].id;
      this.filtersForm.get('RFQSummaryNumber')!.setValue([rfqSummaryNum]);
    } else if (this.RFQSummaryNumItem?.length && this.RFQSummaryNumItem.length > 1) {
      this.filtersForm.get('RFQSummaryNumber')!.setValue(this.RFQSummaryNumItem.map(item => item.id));
      this.updateSelectedRFQSummaryNumberLabel();
    }
  }

  onMPDocNumClear() {
    this.filtersForm.get('MPDocumentNumber')?.setValue('')
    this.RFQSummaryNumItem = [];
    this.filtersForm.get('RFQSummaryNumber')!.setValue([]);
  }

  onRFQSummaryNumClear() {
    this.filtersForm.get('RFQSummaryNumber')!.setValue([]);
  }

  onIndividualCheckboxChange(): void {
    console.log('Checkbox state changed:', this.csvHeaderKeys.value);
  }

  private formatAndSaveReport(reportData: RfqSummaryReportRow[]) {
    let selectedHeaderKeys: RFQReportHeaderKey[] = RFQ_HEADER_KEYS.map(
      (key) => key
    );

    const formCsvHeaderKeys: FiltersForm['csvHeaderKeys'] =
      this.csvHeaderKeys.value;
    selectedHeaderKeys = RFQ_HEADER_KEYS.filter(
      (key, index) => formCsvHeaderKeys[index]
    );

    let datetimeFormatRegex: RegExp = /^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}$/;

    const csv = Papa.unparse({
      fields: selectedHeaderKeys.map((key) => RFQ_HEADER_MAPPING[key]),
      data: reportData.map((rowObj) => {
        return selectedHeaderKeys.map((key) => {
          if (rowObj[key] && key == 'PurchaseExecutiveName') {
            return rowObj[key]?.toString().replace(/\\r|\\n|\r|\n/g, '').trim();
          }
          if (key === 'QuotationAttachment') {
            const value = rowObj[key];
            if (value === true || value === 'true') return 'YES';
            else if (value === false || value === 'false') return 'NO';
          }
          if (rowObj[key] && (key == 'PlannedDeliveryDate' || key == 'CreatedOnDate' || key == 'SubmittedDate')) {
            const value = rowObj[key];
            if (value && typeof value === 'string') {
              if(datetimeFormatRegex.test(value)) {
                rowObj[key] = value.substring(0, 10);
              } else if (!isNaN(new Date(value).getTime())) {
                rowObj[key] = this.datePipe.transform(value, 'dd/MM/yyyy');
              }
            }
            return rowObj[key];
          }
          else return rowObj[key] ?? null;
        });
      }),
    });

    // Download csv file
    var blob = new Blob([csv]);
    var a = window.document.createElement('a');
    a.href = window.URL.createObjectURL(blob);
    if (this.isUserPE) {
      a.download = `${this.csvFileNamePrefix}PE-report-${moment().format(
        'YYYYMMDDHHmmss'
      )}.csv`;
    } else {
      a.download = `${this.csvFileNamePrefix}RO-report-${moment().format(
        'YYYYMMDDHHmmss'
      )}.csv`;
    }

    document.body.appendChild(a);
    a.click(); // IE: "Access is denied"; see: https://connect.microsoft.com/IE/feedback/details/797361/ie-10-treats-blob-url-as-cross-origin-and-denies-access
    document.body.removeChild(a);
  }

  public get alertType(): typeof AlertType {
    return AlertType;
  }
  public get ValidationError(): typeof ValidationError {
    return ValidationError;
  }

  public get RFQ_HEADER_KEYS(): typeof RFQ_HEADER_KEYS {
    return RFQ_HEADER_KEYS;
  }

  public get RFQ_HEADER_MAPPING(): typeof RFQ_HEADER_MAPPING {
    return RFQ_HEADER_MAPPING;
  }
}
